import { CheckCircle2, Circle, Copy, Download, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Button, ConfirmDialog, cx, ErrorNote, Spinner, toast } from "../components/ui";
import { api, useApi, type Me } from "../lib/api";

interface Sso {
  signInMode: "cognito" | "federated";
  idpReady: boolean;
  idpMetadataUrl: string | null;
  idpUpdatedAt: string | null;
  idpVerifiedAt: string | null;
  idpEnforced: boolean;
  providers: string[];
  sp: { entityId: string; replyUrl: string; signOnUrl: string };
  instructions: string;
}

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/markdown" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Tenant admin: single sign-on with the organization's Microsoft Entra (design §4.2). */
export default function SignInSettings({ me }: { me?: Me }) {
  const { data, error, loading, setData } = useApi<Sso>("/admin/sso");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"enforce" | "remove" | null>(null);

  const run = async (label: string, fn: () => Promise<Sso>, done: string) => {
    setBusy(label);
    setProblem(null);
    try {
      setData(await fn());
      toast(done);
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  if (loading && !data) return <Spinner />;
  if (error || !data) return <ErrorNote>{error ?? "Couldn't load sign-in settings"}</ErrorNote>;
  const d = data;
  const tenantId = location.hostname.split(".")[0];
  const copy = (v: string) => navigator.clipboard.writeText(v).then(() => toast("Copied"));
  const step = (done: boolean, n: number) => (done ? <CheckCircle2 className="size-5 text-good shrink-0" aria-label="Done" /> : <span className="size-5 rounded-full border border-line text-xs text-muted inline-flex items-center justify-center shrink-0">{n}</span>);

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-ink">Sign-in</h1>
        <p className="text-sm text-muted">
          {d.idpEnforced ? "People sign in with your organization's Microsoft account only." : d.idpReady && d.signInMode === "federated" ? "Single sign-on is set up. Portal accounts still work until you turn on single sign-on for everyone." : "People sign in with portal accounts (email invitation and authenticator app)."}
        </p>
      </div>
      {problem && <ErrorNote>{problem}</ErrorNote>}
      {d.signInMode === "cognito" && (
        <div className="rounded-xl border border-warn bg-warn-soft text-warn text-sm p-4">Your tenant uses portal accounts. You can prepare single sign-on here; Intellias switches the tenant to single sign-on when you're ready.</div>
      )}

      <section className="rounded-xl border border-line bg-surface p-5 space-y-3">
        <div className="flex items-center gap-2">
          {step(d.idpReady, 1)}
          <h2 className="font-display font-semibold text-ink">Create the Entra app (your IT team)</h2>
        </div>
        <p className="text-sm text-ink-2">A non-gallery enterprise application with SAML. Give your IT team these values, or download the full instructions.</p>
        <dl className="text-sm divide-y divide-line rounded-lg border border-line">
          {[
            ["Identifier (Entity ID)", d.sp.entityId],
            ["Reply URL (ACS)", d.sp.replyUrl],
            ["Sign on URL", d.sp.signOnUrl],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 px-3 py-2">
              <dt className="w-44 text-muted shrink-0">{k}</dt>
              <dd className="flex-1 min-w-0 font-mono text-xs break-all">{v}</dd>
              <Button variant="ghost" aria-label={`Copy ${k}`} onClick={() => void copy(v)}>
                <Copy className="size-4" aria-hidden />
              </Button>
            </div>
          ))}
        </dl>
        <Button onClick={() => download(`entra-sso-${tenantId}.md`, d.instructions)}>
          <Download className="size-4" aria-hidden /> Download instructions
        </Button>
      </section>

      <section className="rounded-xl border border-line bg-surface p-5 space-y-3">
        <div className="flex items-center gap-2">
          {step(d.idpReady, 2)}
          <h2 className="font-display font-semibold text-ink">Connect it</h2>
        </div>
        <p className="text-sm text-ink-2">Paste the Entra app's <strong>App Federation Metadata Url</strong> (SAML Certificates section). It isn't a secret.</p>
        {d.idpMetadataUrl && (
          <p className="text-xs text-muted break-all">
            Connected: {d.idpMetadataUrl} · {d.idpUpdatedAt && when(d.idpUpdatedAt)}
          </p>
        )}
        <div className="flex gap-2">
          <input className="flex-1 h-9 rounded-lg border border-line bg-surface px-3 text-sm" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://login.microsoftonline.com/…/federationmetadata.xml?appid=…" />
          <Button variant="primary" busy={busy === "save"} disabled={!url.trim()} onClick={() => void run("save", () => api<Sso>("/admin/sso", { method: "PUT", body: { metadataUrl: url.trim() } }), "Single sign-on connected")}>
            {d.idpReady ? "Replace" : "Save"}
          </Button>
        </div>
      </section>

      <section className={cx("rounded-xl border border-line bg-surface p-5 space-y-3", !d.idpReady && "opacity-60")}>
        <div className="flex items-center gap-2">
          {step(!!d.idpVerifiedAt, 3)}
          <h2 className="font-display font-semibold text-ink">Try it</h2>
        </div>
        {d.idpVerifiedAt ? (
          <p className="text-sm text-good">Someone signed in with your organization on {when(d.idpVerifiedAt)}. Single sign-on works.</p>
        ) : (
          <p className="text-sm text-ink-2">
            Sign out, then choose <strong>Sign in with your organization</strong> (or use a private browser window). The person must already be added under Users with the same email address Entra sends. This step proves it works before portal accounts are turned off.
          </p>
        )}
      </section>

      <section className={cx("rounded-xl border bg-surface p-5 space-y-3", d.idpEnforced ? "border-good" : "border-line", (!d.idpVerifiedAt || d.signInMode !== "federated") && !d.idpEnforced && "opacity-60")}>
        <div className="flex items-center gap-2">
          {step(d.idpEnforced, 4)}
          <h2 className="font-display font-semibold text-ink">Turn on single sign-on for everyone</h2>
        </div>
        <p className="text-sm text-ink-2">Portal accounts stop working for your tenant; everyone signs in with your organization. You can turn this off again here.</p>
        <div className="flex flex-wrap gap-2">
          {d.idpEnforced ? (
            <Button busy={busy === "enforce"} onClick={() => void run("enforce", () => api<Sso>("/admin/sso/enforce", { body: { on: false } }), "Portal accounts work again")}>
              Allow portal accounts again
            </Button>
          ) : (
            <Button variant="primary" disabled={!d.idpVerifiedAt || d.signInMode !== "federated"} onClick={() => setConfirm("enforce")}>
              <ShieldCheck className="size-4" aria-hidden /> Turn on for everyone
            </Button>
          )}
          {d.idpReady && (
            <Button variant="ghost" onClick={() => setConfirm("remove")}>
              Disconnect single sign-on
            </Button>
          )}
        </div>
      </section>

      {confirm === "enforce" && (
        <ConfirmDialog title="Turn on single sign-on for everyone?" confirm="Turn on" onClose={() => setConfirm(null)} onConfirm={() => run("enforce", () => api<Sso>("/admin/sso/enforce", { body: { on: true } }), "Single sign-on is on for everyone")}>
          Portal accounts will stop working for {me?.tenant?.name ?? "your tenant"}. Everyone, including you, signs in with your organization from now on.
        </ConfirmDialog>
      )}
      {confirm === "remove" && (
        <ConfirmDialog title="Disconnect single sign-on?" confirm="Disconnect" danger onClose={() => setConfirm(null)} onConfirm={() => run("remove", () => api<Sso>("/admin/sso", { method: "DELETE" }), "Single sign-on disconnected")}>
          Everyone goes back to portal accounts. People who only ever signed in with your organization will need a portal invitation.
        </ConfirmDialog>
      )}
      <p className="text-xs text-muted flex items-center gap-1">
        <Circle className="size-3" aria-hidden /> Allowed now: {d.providers.map((p) => (p === "COGNITO" ? "portal accounts" : "your organization's sign-in")).join(" and ")}
      </p>
    </div>
  );
}
